import { useProctoredAttempt } from '../components/focus-guard/FocusGuardContext';
import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { MainLayout } from '../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
    Play, 
    Pause, 
    ChevronLeft, 
    ChevronRight, 
    MessageSquare,
    Send,
    CheckCircle,
    XCircle,
    Target,
    BookOpen,
    Lightbulb,
    HelpCircle,
    Award,
    RefreshCw,
    SkipForward,
    Loader2,
    Presentation,
    Brain,
    Clock,
    Sparkles,
    GraduationCap,
    Zap,
    ArrowRight,
    Home,
    Volume2,
    ChevronDown
} from 'lucide-react';

const InteractiveLearn = () => {
    const { topicId } = useParams();
    const navigate = useNavigate();
    const { api } = useAuth();
    
    // Session state
    const [session, setSession] = useState(null);
    const [loading, setLoading] = useState(true);
    const [phase, setPhase] = useState('loading');
    useProctoredAttempt({ active: phase === 'baseline' || phase === 'assessment', label: 'assessment' });
    
    // Topic info
    const [topicInfo, setTopicInfo] = useState(null);
    
    // Baseline assessment
    const [baselineQuestion, setBaselineQuestion] = useState(null);
    const [knowledgeLevel, setKnowledgeLevel] = useState('basic');
    const [welcomeMessage, setWelcomeMessage] = useState('');
    
    // Learning content
    const [currentContent, setCurrentContent] = useState(null);
    const [currentSlide, setCurrentSlide] = useState(null);
    const [aiExplanation, setAiExplanation] = useState('');
    const [progress, setProgress] = useState({ current_section: 0, total_sections: 0, percentage: 0, current_slide: 1, total_slides: 1 });
    
    // Slide auto-advance
    const [autoAdvance, setAutoAdvance] = useState(false);
    const [autoAdvanceInterval, setAutoAdvanceInterval] = useState(null);
    const SLIDE_DURATION = 12000;
    
    // Assessment
    const [assessmentQuestions, setAssessmentQuestions] = useState([]);
    const [currentAnswers, setCurrentAnswers] = useState({});
    const [assessmentResults, setAssessmentResults] = useState(null);
    const [submittingAssessment, setSubmittingAssessment] = useState(false);
    
    // Chat
    const [showChat, setShowChat] = useState(false);
    const [chatMessage, setChatMessage] = useState('');
    const [chatHistory, setChatHistory] = useState([]);
    const [sendingMessage, setSendingMessage] = useState(false);
    
    // UI State
    const [isNavOpen, setIsNavOpen] = useState(false);
    const chatEndRef = useRef(null);

    useEffect(() => {
        startLearningSession();
        return () => {
            if (autoAdvanceInterval) clearInterval(autoAdvanceInterval);
        };
    }, [topicId]);

    useEffect(() => {
        if (autoAdvance && phase === 'learning' && currentSlide) {
            const interval = setInterval(() => {
                handleNext();
            }, SLIDE_DURATION);
            setAutoAdvanceInterval(interval);
            return () => clearInterval(interval);
        } else if (autoAdvanceInterval) {
            clearInterval(autoAdvanceInterval);
            setAutoAdvanceInterval(null);
        }
    }, [autoAdvance, phase, currentSlide?.slide_number]);

    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [chatHistory]);

    const startLearningSession = async () => {
        try {
            setLoading(true);
            const response = await api.post(`/learn/start-topic/${topicId}`);
            setSession(response.data);
            setTopicInfo(response.data.topic);
            setBaselineQuestion(response.data.baseline_question);
            setPhase('baseline');
        } catch (error) {
            toast.error('Failed to start learning session', {
                description: error.response?.data?.detail || 'Topic may not be available'
            });
            navigate('/training');
        } finally {
            setLoading(false);
        }
    };

    const submitBaseline = async () => {
        try {
            setLoading(true);
            const response = await api.post(`/learn/${session.session_id}/baseline`, {
                knowledge_level: knowledgeLevel,
                topics_familiar: [],
                specific_interests: null
            });
            
            setWelcomeMessage(response.data.welcome_message);
            setPhase('learning');
            
            await fetchContent();
        } catch (error) {
            toast.error('Failed to submit assessment');
        } finally {
            setLoading(false);
        }
    };

    const fetchContent = async () => {
        try {
            const response = await api.get(`/learn/${session.session_id}/content`);
            setCurrentContent(response.data.section);
            setCurrentSlide(response.data.slide);
            setAiExplanation(response.data.ai_explanation);
            setProgress(response.data.progress);
            
            if (response.data.phase === 'assessment') {
                setPhase('assessment');
            }
        } catch (error) {
            console.error('Failed to fetch content:', error);
        }
    };

    const handleNext = async () => {
        try {
            const response = await api.post(`/learn/${session.session_id}/next`);
            
            if (response.data.phase === 'assessment') {
                setPhase('assessment');
                setAssessmentQuestions(response.data.assessment?.questions || []);
            } else {
                setCurrentContent(response.data.section);
                setCurrentSlide(response.data.slide);
                setAiExplanation(response.data.ai_explanation);
                setProgress(response.data.progress);
            }
        } catch (error) {
            toast.error('Failed to load next content');
        }
    };

    const handlePrevious = async () => {
        try {
            const response = await api.post(`/learn/${session.session_id}/previous`);
            setCurrentContent(response.data.section);
            setCurrentSlide(response.data.slide);
            setAiExplanation(response.data.ai_explanation);
            setProgress(response.data.progress);
        } catch (error) {
            toast.error('Failed to go back');
        }
    };

    const submitAssessment = async () => {
        setSubmittingAssessment(true);
        try {
            const answers = Object.entries(currentAnswers).map(([qId, answer]) => ({
                question_id: parseInt(qId),
                answer
            }));
            
            const response = await api.post(`/learn/${session.session_id}/submit-assessment`, { answers });
            setAssessmentResults(response.data);
            
            if (response.data.passed) {
                setPhase('completed');
                toast.success('Congratulations!', { description: 'You passed the assessment!' });
            }
        } catch (error) {
            toast.error('Failed to submit assessment');
        } finally {
            setSubmittingAssessment(false);
        }
    };

    const retryAssessment = async () => {
        try {
            const response = await api.post(`/learn/${session.session_id}/retry-assessment`);
            setAssessmentQuestions(response.data.assessment?.questions || []);
            setCurrentAnswers({});
            setAssessmentResults(null);
        } catch (error) {
            toast.error('Failed to retry assessment');
        }
    };

    const skipAssessment = async () => {
        try {
            await api.post(`/learn/${session.session_id}/skip-assessment`);
            setPhase('completed');
            toast.info('Assessment skipped');
        } catch (error) {
            toast.error('Failed to skip assessment');
        }
    };

    const sendChatMessage = async () => {
        if (!chatMessage.trim()) return;
        
        setSendingMessage(true);
        const userMessage = chatMessage;
        setChatMessage('');
        setChatHistory(prev => [...prev, { role: 'user', content: userMessage }]);
        
        try {
            const response = await api.post(`/learn/${session.session_id}/ask`, {
                interaction_type: 'question',
                content: userMessage
            });
            
            setChatHistory(prev => [...prev, { role: 'assistant', content: response.data.response }]);
        } catch (error) {
            setChatHistory(prev => [...prev, { 
                role: 'assistant', 
                content: 'Sorry, I could not process your question. Please try again.' 
            }]);
        } finally {
            setSendingMessage(false);
        }
    };

    // Render slide with beautiful design
    const renderSlide = (slide) => {
        if (!slide) return null;
        
        const slideTypeStyles = {
            title: 'from-primary/20 via-primary/5 to-transparent',
            objectives: 'from-emerald-500/20 via-emerald-500/5 to-transparent',
            content: 'from-sky-500/20 via-sky-500/5 to-transparent',
        };
        
        const gradientClass = slideTypeStyles[slide.type] || slideTypeStyles.content;
        
        return (
            <div className={`bg-gradient-to-br ${gradientClass} rounded-2xl p-8 md:p-10 min-h-[380px] flex flex-col border border-white/10 backdrop-blur-sm shadow-xl`}>
                {slide.type === 'title' ? (
                    <div className="flex-1 flex flex-col items-center justify-center text-center space-y-6">
                        <div className="p-4 rounded-full bg-primary/20 animate-pulse">
                            <Presentation className="h-12 w-12 text-primary" />
                        </div>
                        <h2 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-primary to-sky-500 bg-clip-text text-transparent">
                            {slide.title}
                        </h2>
                        {slide.subtitle && (
                            <p className="text-xl text-muted-foreground max-w-lg">{slide.subtitle}</p>
                        )}
                    </div>
                ) : (
                    <>
                        <div className="flex items-center gap-3 mb-6">
                            {slide.type === 'objectives' && (
                                <div className="p-2 rounded-lg bg-emerald-500/20">
                                    <Target className="h-6 w-6 text-emerald-500" />
                                </div>
                            )}
                            {slide.type === 'content' && (
                                <div className="p-2 rounded-lg bg-sky-500/20">
                                    <BookOpen className="h-6 w-6 text-sky-500" />
                                </div>
                            )}
                            <h3 className="text-2xl font-semibold">{slide.title}</h3>
                        </div>
                        {slide.bullet_points && (
                            <ul className="space-y-4 flex-1">
                                {slide.bullet_points.map((point, idx) => (
                                    <li 
                                        key={`bullet-${idx}-${point.substring(0, 20)}`} 
                                        className="flex items-start gap-4 text-lg animate-fade-in"
                                        style={{ animationDelay: `${idx * 100}ms` }}
                                    >
                                        <div className="w-2 h-2 rounded-full bg-primary mt-3 flex-shrink-0" />
                                        <span className="leading-relaxed">{point}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </>
                )}
            </div>
        );
    };

    // Loading state
    if (loading && !session) {
        return (
            <MainLayout>
                <div className="flex items-center justify-center min-h-[70vh]">
                    <div className="text-center space-y-6">
                        <div className="relative">
                            <div className="w-20 h-20 rounded-full border-4 border-primary/20 animate-pulse" />
                            <Loader2 className="h-20 w-20 absolute top-0 left-0 animate-spin text-primary" />
                        </div>
                        <div>
                            <p className="text-xl font-medium">Preparing your learning experience</p>
                            <p className="text-muted-foreground mt-1">This will only take a moment...</p>
                        </div>
                    </div>
                </div>
            </MainLayout>
        );
    }

    return (
        <MainLayout>
            <div className="max-w-7xl mx-auto animate-fade-in" data-testid="interactive-learn-page">
                {/* Top Navigation Bar */}
                <div className="flex items-center justify-between mb-6 p-4 rounded-xl bg-card/50 backdrop-blur-sm border border-border">
                    <div className="flex items-center gap-4">
                        <Button variant="ghost" size="icon" onClick={() => navigate('/training')} data-testid="back-to-training">
                            <Home className="h-5 w-5" />
                        </Button>
                        <div>
                            <h1 className="text-lg font-semibold flex items-center gap-2">
                                <Brain className="h-5 w-5 text-primary" />
                                {topicInfo?.name || 'Learning Session'}
                            </h1>
                            <p className="text-xs text-muted-foreground">
                                {phase === 'baseline' && 'Knowledge Assessment'}
                                {phase === 'learning' && `Section ${progress.current_section} of ${progress.total_sections}`}
                                {phase === 'assessment' && 'Final Assessment'}
                                {phase === 'completed' && 'Completed!'}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        {phase === 'learning' && (
                            <>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setShowChat(true)}
                                    className="gap-2 hidden sm:flex"
                                    data-testid="ask-question-btn"
                                >
                                    <HelpCircle className="h-4 w-4" />
                                    Ask AI
                                </Button>
                                <Button
                                    variant={autoAdvance ? "default" : "outline"}
                                    size="sm"
                                    onClick={() => setAutoAdvance(!autoAdvance)}
                                    className="gap-2"
                                    data-testid="auto-advance-btn"
                                >
                                    {autoAdvance ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                                    <span className="hidden sm:inline">{autoAdvance ? 'Pause' : 'Auto'}</span>
                                </Button>
                            </>
                        )}
                        <Badge variant="outline" className="gap-1">
                            <Clock className="h-3 w-3" />
                            {topicInfo?.estimated_duration || '~30 min'}
                        </Badge>
                    </div>
                </div>

                {/* Progress Bar */}
                {phase !== 'baseline' && (
                    <div className="mb-6 space-y-2">
                        <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Progress</span>
                            <span className="font-medium">{progress.percentage || 0}%</span>
                        </div>
                        <Progress value={progress.percentage || 0} className="h-2" />
                    </div>
                )}

                {/* PHASE: Baseline Assessment */}
                {phase === 'baseline' && (
                    <div className="max-w-2xl mx-auto">
                        <Card className="border-2 border-primary/20 overflow-hidden">
                            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-sky-500/5 pointer-events-none" />
                            <CardHeader className="text-center pb-2 relative">
                                <div className="mx-auto mb-4 p-4 rounded-full bg-gradient-to-br from-primary/20 to-sky-500/20">
                                    <Sparkles className="h-8 w-8 text-primary" />
                                </div>
                                <CardTitle className="text-2xl">Before We Begin</CardTitle>
                                <p className="text-muted-foreground mt-2">
                                    Help me understand your current knowledge level so I can personalize your experience
                                </p>
                            </CardHeader>
                            <CardContent className="space-y-6 relative">
                                {baselineQuestion?.question && (
                                    <div className="p-4 rounded-xl bg-muted/50 border border-border">
                                        <p className="text-lg font-medium text-center">{baselineQuestion.question}</p>
                                    </div>
                                )}
                                
                                <div className="space-y-3">
                                    <Label className="text-base font-medium">Select your experience level:</Label>
                                    <RadioGroup value={knowledgeLevel} onValueChange={setKnowledgeLevel} className="space-y-3">
                                        {[
                                            { value: 'none', label: 'Complete Beginner', desc: "I've never learned about this topic", icon: '🌱', color: 'emerald' },
                                            { value: 'basic', label: 'Basic Understanding', desc: 'I know the basics but need to learn more', icon: '📚', color: 'blue' },
                                            { value: 'intermediate', label: 'Intermediate', desc: 'I have some experience and want to deepen it', icon: '🎯', color: 'amber' },
                                            { value: 'advanced', label: 'Advanced', desc: "I'm experienced and looking for expert insights", icon: '🚀', color: 'purple' }
                                        ].map(opt => (
                                            <Label
                                                key={opt.value}
                                                htmlFor={opt.value}
                                                className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                                                    knowledgeLevel === opt.value 
                                                        ? 'border-primary bg-primary/5 shadow-lg shadow-primary/10' 
                                                        : 'border-border hover:border-primary/50 hover:bg-muted/50'
                                                }`}
                                                data-testid={`knowledge-level-${opt.value}`}
                                            >
                                                <RadioGroupItem value={opt.value} id={opt.value} className="sr-only" />
                                                <span className="text-2xl">{opt.icon}</span>
                                                <div className="flex-1">
                                                    <p className="font-semibold">{opt.label}</p>
                                                    <p className="text-sm text-muted-foreground">{opt.desc}</p>
                                                </div>
                                                {knowledgeLevel === opt.value && (
                                                    <CheckCircle className="h-5 w-5 text-primary" />
                                                )}
                                            </Label>
                                        ))}
                                    </RadioGroup>
                                </div>
                                
                                <Button 
                                    onClick={submitBaseline} 
                                    className="w-full h-12 text-lg gap-2 bg-gradient-to-r from-primary to-sky-500 hover:opacity-90" 
                                    disabled={loading}
                                    data-testid="start-learning-btn"
                                >
                                    {loading ? (
                                        <Loader2 className="h-5 w-5 animate-spin" />
                                    ) : (
                                        <>
                                            Begin Learning Journey
                                            <ArrowRight className="h-5 w-5" />
                                        </>
                                    )}
                                </Button>
                            </CardContent>
                        </Card>
                    </div>
                )}

                {/* PHASE: Learning */}
                {phase === 'learning' && (
                    <div className="grid gap-6 lg:grid-cols-3">
                        {/* Main Content - Slide */}
                        <div className="lg:col-span-2 space-y-4">
                            {/* Welcome message */}
                            {welcomeMessage && progress.current_section === 1 && (
                                <Card className="bg-gradient-to-r from-primary/10 via-sky-500/5 to-transparent border-primary/20">
                                    <CardContent className="p-4 flex items-start gap-3">
                                        <div className="p-2 rounded-lg bg-primary/20 shrink-0">
                                            <MessageSquare className="h-4 w-4 text-primary" />
                                        </div>
                                        <p className="text-sm leading-relaxed">{welcomeMessage}</p>
                                    </CardContent>
                                </Card>
                            )}
                            
                            {/* Slide Display */}
                            <div className="relative">
                                {renderSlide(currentSlide)}
                                
                                {/* Slide counter overlay */}
                                <div className="absolute bottom-4 right-4 px-3 py-1.5 rounded-full bg-background/80 backdrop-blur-sm border border-border text-sm font-medium">
                                    {progress.current_slide} / {progress.total_slides}
                                </div>
                            </div>
                            
                            {/* Navigation Controls */}
                            <div className="flex items-center justify-between p-4 rounded-xl bg-card/50 border border-border">
                                <Button
                                    variant="outline"
                                    onClick={handlePrevious}
                                    disabled={progress.current_slide === 1 && progress.current_section === 1}
                                    className="gap-2"
                                    data-testid="prev-slide-btn"
                                >
                                    <ChevronLeft className="h-4 w-4" />
                                    <span className="hidden sm:inline">Previous</span>
                                </Button>
                                
                                <div className="flex items-center gap-2">
                                    {/* Slide indicators */}
                                    <div className="hidden md:flex items-center gap-1">
                                        {Array.from({ length: Math.min(progress.total_slides, 10) }, (_, i) => (
                                            <div
                                                key={i}
                                                className={`w-2 h-2 rounded-full transition-all ${
                                                    i + 1 === progress.current_slide 
                                                        ? 'bg-primary w-4' 
                                                        : i + 1 < progress.current_slide 
                                                            ? 'bg-primary/50' 
                                                            : 'bg-muted'
                                                }`}
                                            />
                                        ))}
                                        {progress.total_slides > 10 && (
                                            <span className="text-xs text-muted-foreground ml-1">+{progress.total_slides - 10}</span>
                                        )}
                                    </div>
                                </div>
                                
                                <Button
                                    onClick={handleNext}
                                    className="gap-2 bg-gradient-to-r from-primary to-sky-500 hover:opacity-90"
                                    data-testid="next-slide-btn"
                                >
                                    <span className="hidden sm:inline">Next</span>
                                    <ChevronRight className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                        
                        {/* Sidebar */}
                        <div className="space-y-4">
                            {/* AI Explanation */}
                            <Card className="border-primary/20">
                                <CardHeader className="pb-3">
                                    <CardTitle className="text-base flex items-center gap-2">
                                        <div className="p-1.5 rounded-lg bg-primary/20">
                                            <Brain className="h-4 w-4 text-primary" />
                                        </div>
                                        AI Tutor Insight
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <ScrollArea className="h-[180px] pr-2">
                                        <p className="text-sm leading-relaxed text-muted-foreground">
                                            {aiExplanation || 'Loading personalized explanation...'}
                                        </p>
                                    </ScrollArea>
                                </CardContent>
                            </Card>
                            
                            {/* Current Section Details */}
                            {currentContent && (
                                <Card>
                                    <CardHeader className="pb-3">
                                        <CardTitle className="text-base flex items-center gap-2">
                                            <BookOpen className="h-4 w-4 text-sky-500" />
                                            {currentContent.title}
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <ScrollArea className="h-[160px] pr-2">
                                            <p className="text-sm text-muted-foreground leading-relaxed">
                                                {currentContent.content?.substring(0, 400)}...
                                            </p>
                                            
                                            {currentContent.key_points?.length > 0 && (
                                                <div className="mt-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                                                    <p className="text-xs font-medium mb-2 flex items-center gap-1 text-amber-600">
                                                        <Lightbulb className="h-3 w-3" />
                                                        Key Points
                                                    </p>
                                                    <ul className="space-y-1">
                                                        {currentContent.key_points.slice(0, 3).map((point, idx) => (
                                                            <li key={`key-pt-${idx}-${point.substring(0, 15)}`} className="text-xs flex items-start gap-2">
                                                                <span className="text-amber-500 mt-0.5">•</span>
                                                                {point}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                        </ScrollArea>
                                    </CardContent>
                                </Card>
                            )}
                            
                            {/* Learning Objectives */}
                            <Card className="bg-emerald-500/5 border-emerald-500/20">
                                <CardHeader className="pb-3">
                                    <CardTitle className="text-base flex items-center gap-2">
                                        <Target className="h-4 w-4 text-emerald-500" />
                                        Learning Goals
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <ul className="space-y-2">
                                        {topicInfo?.learning_objectives?.slice(0, 4).map((obj, idx) => (
                                            <li key={`obj-${idx}-${obj.substring(0, 15)}`} className="text-xs flex items-start gap-2">
                                                <CheckCircle className="h-3 w-3 text-emerald-500 mt-0.5 shrink-0" />
                                                <span className="text-muted-foreground">{obj}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </CardContent>
                            </Card>
                            
                            {/* Mobile Ask Button */}
                            <Button
                                variant="outline"
                                className="w-full sm:hidden gap-2"
                                onClick={() => setShowChat(true)}
                            >
                                <HelpCircle className="h-4 w-4" />
                                Ask a Question
                            </Button>
                        </div>
                    </div>
                )}

                {/* PHASE: Assessment */}
                {phase === 'assessment' && !assessmentResults && (
                    <div className="max-w-3xl mx-auto">
                        <Card className="overflow-hidden">
                            <CardHeader className="bg-gradient-to-r from-primary/10 to-sky-500/10 border-b">
                                <CardTitle className="flex items-center gap-3">
                                    <div className="p-2 rounded-lg bg-primary/20">
                                        <GraduationCap className="h-5 w-5 text-primary" />
                                    </div>
                                    Knowledge Assessment
                                </CardTitle>
                                <p className="text-sm text-muted-foreground mt-1">
                                    Answer the questions below to test your understanding. You need 70% to pass.
                                </p>
                            </CardHeader>
                            <CardContent className="p-6 space-y-6">
                                {assessmentQuestions.map((q, idx) => (
                                    <div key={q.id || idx} className="p-5 rounded-xl border bg-card hover:border-primary/30 transition-colors" data-testid={`question-${idx}`}>
                                        <div className="flex items-start justify-between gap-4 mb-4">
                                            <p className="font-medium text-lg">
                                                <span className="text-primary mr-2">Q{idx + 1}.</span>
                                                {q.question}
                                            </p>
                                            <Badge variant="secondary" className="shrink-0">{q.type === 'mcq' ? 'Multiple Choice' : 'True/False'}</Badge>
                                        </div>
                                        
                                        {q.scenario && (
                                            <p className="text-sm text-muted-foreground mb-4 italic p-3 bg-muted/50 rounded-lg border-l-2 border-primary">
                                                {q.scenario}
                                            </p>
                                        )}
                                        
                                        {q.options ? (
                                            <RadioGroup
                                                value={currentAnswers[q.id] || ''}
                                                onValueChange={(val) => setCurrentAnswers(prev => ({ ...prev, [q.id]: val }))}
                                                className="space-y-2"
                                            >
                                                {q.options.map((opt, oIdx) => (
                                                    <Label
                                                        key={oIdx}
                                                        htmlFor={`q${q.id}-${oIdx}`}
                                                        className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-all ${
                                                            currentAnswers[q.id] === opt
                                                                ? 'border-primary bg-primary/5 shadow-sm'
                                                                : 'hover:border-primary/50 hover:bg-muted/30'
                                                        }`}
                                                    >
                                                        <RadioGroupItem value={opt} id={`q${q.id}-${oIdx}`} />
                                                        <span className="text-sm">{opt}</span>
                                                    </Label>
                                                ))}
                                            </RadioGroup>
                                        ) : q.type === 'true_false' && (
                                            <RadioGroup
                                                value={currentAnswers[q.id]?.toString() || ''}
                                                onValueChange={(val) => setCurrentAnswers(prev => ({ ...prev, [q.id]: val === 'true' }))}
                                                className="flex gap-4"
                                            >
                                                <Label className={`flex-1 flex items-center justify-center gap-2 p-4 rounded-lg border cursor-pointer transition-all ${currentAnswers[q.id] === true ? 'border-emerald-500 bg-emerald-500/10' : 'hover:border-emerald-500/50'}`}>
                                                    <RadioGroupItem value="true" id={`q${q.id}-true`} />
                                                    <CheckCircle className="h-4 w-4 text-emerald-500" />
                                                    True
                                                </Label>
                                                <Label className={`flex-1 flex items-center justify-center gap-2 p-4 rounded-lg border cursor-pointer transition-all ${currentAnswers[q.id] === false ? 'border-red-500 bg-red-500/10' : 'hover:border-red-500/50'}`}>
                                                    <RadioGroupItem value="false" id={`q${q.id}-false`} />
                                                    <XCircle className="h-4 w-4 text-red-500" />
                                                    False
                                                </Label>
                                            </RadioGroup>
                                        )}
                                    </div>
                                ))}
                                
                                <div className="flex gap-3 pt-4">
                                    <Button 
                                        onClick={submitAssessment} 
                                        className="flex-1 h-12 gap-2 bg-gradient-to-r from-primary to-sky-500 hover:opacity-90"
                                        disabled={submittingAssessment || Object.keys(currentAnswers).length < assessmentQuestions.length}
                                        data-testid="submit-assessment-btn"
                                    >
                                        {submittingAssessment ? (
                                            <Loader2 className="h-5 w-5 animate-spin" />
                                        ) : (
                                            <>
                                                <Send className="h-5 w-5" />
                                                Submit Answers
                                            </>
                                        )}
                                    </Button>
                                    <Button variant="outline" onClick={skipAssessment} className="gap-2 h-12" data-testid="skip-assessment-btn">
                                        <SkipForward className="h-4 w-4" />
                                        Skip
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                )}

                {/* Assessment Results - Failed */}
                {assessmentResults && !assessmentResults.passed && (
                    <div className="max-w-3xl mx-auto">
                        <Card className="border-amber-500/30 overflow-hidden">
                            <CardHeader className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border-b border-amber-500/20">
                                <CardTitle className="flex items-center gap-3 text-amber-500">
                                    <div className="p-2 rounded-lg bg-amber-500/20">
                                        <Zap className="h-5 w-5" />
                                    </div>
                                    Almost There!
                                </CardTitle>
                                <p className="text-muted-foreground">
                                    You scored <span className="font-semibold text-amber-500">{assessmentResults.score}%</span> (need {assessmentResults.passing_score}% to pass)
                                </p>
                            </CardHeader>
                            <CardContent className="p-6 space-y-4">
                                {/* Adaptive Recommendations */}
                                {assessmentResults.recommendations?.length > 0 && (
                                    <div className="p-4 rounded-xl bg-sky-500/10 border border-sky-500/20">
                                        <h4 className="font-medium text-sky-500 mb-2 flex items-center gap-2">
                                            <Lightbulb className="h-4 w-4" />
                                            Personalized Tips for You
                                        </h4>
                                        <ul className="space-y-1">
                                            {assessmentResults.recommendations.map((rec, idx) => (
                                                <li key={idx} className="text-sm flex items-start gap-2">
                                                    <span className="text-sky-500 mt-0.5">•</span>
                                                    {rec}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                
                                <ScrollArea className="h-[250px] pr-4">
                                    <div className="space-y-3">
                                        {assessmentResults.results?.map((r, idx) => (
                                            <div 
                                                key={idx} 
                                                className={`p-4 rounded-xl border ${
                                                    r.is_correct 
                                                        ? 'bg-emerald-500/5 border-emerald-500/30' 
                                                        : 'bg-red-500/5 border-red-500/30'
                                                }`}
                                            >
                                                <div className="flex items-start gap-3">
                                                    {r.is_correct ? (
                                                        <CheckCircle className="h-5 w-5 text-emerald-500 mt-0.5 shrink-0" />
                                                    ) : (
                                                        <XCircle className="h-5 w-5 text-red-500 mt-0.5 shrink-0" />
                                                    )}
                                                    <div className="flex-1 space-y-2">
                                                        <p className="font-medium">{r.question}</p>
                                                        {!r.is_correct && (
                                                            <div className="text-sm space-y-1">
                                                                <p className="text-red-400">Your answer: {String(r.user_answer)}</p>
                                                                <p className="text-emerald-400">Correct: {String(r.correct_answer)}</p>
                                                            </div>
                                                        )}
                                                        {r.explanation && (
                                                            <p className="text-xs text-muted-foreground p-2 bg-muted/50 rounded-lg">{r.explanation}</p>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </ScrollArea>
                                
                                <div className="flex gap-3 pt-2">
                                    <Button onClick={retryAssessment} className="flex-1 gap-2" data-testid="retry-assessment-btn">
                                        <RefreshCw className="h-4 w-4" />
                                        Try Again
                                    </Button>
                                    <Button variant="outline" onClick={skipAssessment} className="gap-2">
                                        <SkipForward className="h-4 w-4" />
                                        Skip & Continue
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                )}

                {/* PHASE: Completed */}
                {phase === 'completed' && (
                    <div className="max-w-2xl mx-auto text-center">
                        <Card className="border-emerald-500/30 overflow-hidden">
                            <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/10 via-primary/5 to-sky-500/10 pointer-events-none" />
                            <CardContent className="py-16 relative">
                                <div className="mb-6 relative inline-block">
                                    <div className="absolute inset-0 animate-ping bg-yellow-500/30 rounded-full" style={{ animationDuration: '2s' }} />
                                    <div className="relative p-6 rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 shadow-2xl shadow-yellow-500/30">
                                        <Award className="h-12 w-12 text-white" />
                                    </div>
                                </div>
                                
                                <h2 className="text-3xl font-bold mb-3 bg-gradient-to-r from-emerald-400 to-sky-400 bg-clip-text text-transparent">
                                    Congratulations!
                                </h2>
                                <p className="text-lg text-muted-foreground mb-8 max-w-md mx-auto">
                                    {assessmentResults?.passed 
                                        ? `You've completed "${topicInfo?.name}" with a score of ${assessmentResults.score}%!`
                                        : `You've completed reviewing "${topicInfo?.name}".`
                                    }
                                </p>
                                
                                {assessmentResults?.passed && (
                                    <div className="flex justify-center gap-2 mb-8">
                                        {[...Array(5)].map((_, i) => (
                                            <div 
                                                key={i} 
                                                className="text-2xl animate-bounce"
                                                style={{ animationDelay: `${i * 100}ms` }}
                                            >
                                                ⭐
                                            </div>
                                        ))}
                                    </div>
                                )}
                                
                                <div className="flex gap-4 justify-center">
                                    <Button 
                                        onClick={() => navigate('/dashboard')} 
                                        variant="outline"
                                        className="gap-2"
                                        data-testid="go-to-dashboard"
                                    >
                                        <Home className="h-4 w-4" />
                                        Dashboard
                                    </Button>
                                    <Button 
                                        onClick={() => navigate('/training')}
                                        className="gap-2 bg-gradient-to-r from-primary to-sky-500 hover:opacity-90"
                                        data-testid="continue-learning"
                                    >
                                        Continue Learning
                                        <ArrowRight className="h-4 w-4" />
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                )}

                {/* Chat Dialog */}
                <Dialog open={showChat} onOpenChange={setShowChat}>
                    <DialogContent className="max-w-lg sm:max-w-xl">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <div className="p-2 rounded-lg bg-primary/20">
                                    <Brain className="h-5 w-5 text-primary" />
                                </div>
                                Ask Your AI Tutor
                            </DialogTitle>
                            <DialogDescription>
                                I&apos;m here to help clarify anything you&apos;re learning. Ask away!
                            </DialogDescription>
                        </DialogHeader>
                        
                        <ScrollArea className="h-[350px] pr-4 mt-4">
                            <div className="space-y-4">
                                {chatHistory.length === 0 && (
                                    <div className="text-center py-8">
                                        <HelpCircle className="h-12 w-12 mx-auto mb-3 text-muted-foreground/50" />
                                        <p className="text-muted-foreground">Ask me anything about the topic!</p>
                                        <div className="mt-4 flex flex-wrap gap-2 justify-center">
                                            {['Explain this simply', 'Give me an example', 'Why is this important?'].map(q => (
                                                <Button 
                                                    key={q}
                                                    variant="outline" 
                                                    size="sm"
                                                    onClick={() => { setChatMessage(q); }}
                                                    className="text-xs"
                                                >
                                                    {q}
                                                </Button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {chatHistory.map((msg, idx) => (
                                    <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                                        <div className={`max-w-[85%] p-4 rounded-2xl ${
                                            msg.role === 'user' 
                                                ? 'bg-primary text-primary-foreground rounded-br-sm' 
                                                : 'bg-muted rounded-bl-sm'
                                        }`}>
                                            <p className="text-sm leading-relaxed">{msg.content}</p>
                                        </div>
                                    </div>
                                ))}
                                {sendingMessage && (
                                    <div className="flex justify-start">
                                        <div className="bg-muted p-4 rounded-2xl rounded-bl-sm">
                                            <div className="flex items-center gap-2">
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                                <span className="text-sm">Thinking...</span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                <div ref={chatEndRef} />
                            </div>
                        </ScrollArea>
                        
                        <div className="flex gap-2 mt-4">
                            <Textarea
                                value={chatMessage}
                                onChange={(e) => setChatMessage(e.target.value)}
                                placeholder="Type your question..."
                                rows={2}
                                className="resize-none"
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        sendChatMessage();
                                    }
                                }}
                                data-testid="chat-input"
                            />
                            <Button 
                                onClick={sendChatMessage} 
                                disabled={sendingMessage || !chatMessage.trim()}
                                className="shrink-0 h-auto"
                                data-testid="send-chat-btn"
                            >
                                <Send className="h-4 w-4" />
                            </Button>
                        </div>
                    </DialogContent>
                </Dialog>
            </div>
        </MainLayout>
    );
};

export default InteractiveLearn;
